import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import moment from "moment-timezone";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeading from "../../components/common/PageHeading";
import SpringContainer from "../../components/containers/SpringContainer";
import Button from "../../components/form/Button";
import { ROUTE_RENEW_PLANS } from "../../constants/routes";
import useSelectedAccount from "../../hooks/useSelectedAccount";
import { useAppSelector } from "../../redux/hooks";
import { useGetSubscriptionQuery } from "../../redux/services/subscription";
import { RootState } from "../../redux/store";
import { getStripeBillingURL } from "../../stripe";
import { formatDateUS, getDaysLeft } from "../../utils";

const Billing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const authToken = useAppSelector((state: RootState) => state.auth.token);
  const { selectedAccount, accountId, timezone } = useSelectedAccount();
  const { data: subscription } = useGetSubscriptionQuery(
    {
      accountId,
    },
    { skip: !accountId }
  );

  const manageBilling = async () => {
    if (selectedAccount?.stripeCustomerId && authToken) {
      setLoading(true);
      const billingURL = await getStripeBillingURL(
        selectedAccount.stripeCustomerId,
        authToken
      );
      setLoading(false);
      window.open(billingURL, "_blank");
    }
  };

  // TODO: Pass timezone in API request to get timezone adjusted dates
  const startDate = useMemo(
    () => moment.tz(subscription?.currentPeriodStart, timezone),
    [subscription?.currentPeriodStart, timezone]
  );
  const endDate = useMemo(
    () => moment.tz(subscription?.currentPeriodEnd, timezone),
    [subscription?.currentPeriodEnd, timezone]
  );
  const subscriptionDaysRemaining = useMemo(() => {
    const currentDate = moment().tz(timezone);
    return getDaysLeft(endDate, currentDate);
  }, [endDate, timezone]);

  /* TODO: If passing timezone in API, use this code
  const startDate = subscription?.currentPeriodStart;
  const endDate = subscription?.currentPeriodEnd;
  const subscriptionDaysRemaining = useMemo(() => {
    const currentDate = moment().tz(timezone);
    return getDaysLeft(endDate, currentDate);
  }, [endDate, timezone]);
  */

  const renderSubscriptionMessage = () => {
    const status = subscription?.status;
    if (!status) return null;

    if (status === "trialing") {
      return subscriptionDaysRemaining > 0 ? (
        <div className="mt-8">
          The trial ends in{" "}
          <b>
            {subscriptionDaysRemaining}{" "}
            {subscriptionDaysRemaining > 1 ? "days" : "day"}.
          </b>
        </div>
      ) : (
        <div className="mt-8">The trial has ended.</div>
      );
    } else if (
      status === "active" &&
      subscription?.canceledAt &&
      subscription?.cancelAtPeriodEnd
    ) {
      return (
        <div className="mt-8">
          The plan ends on <b>{formatDateUS(subscription?.currentPeriodEnd)}</b>
          .
        </div>
      );
    } else if (status === "active" && subscriptionDaysRemaining > 0) {
      return (
        <div className="mt-8">
          The billing cycle renews in{" "}
          <b>
            {subscriptionDaysRemaining}{" "}
            {subscriptionDaysRemaining > 1 ? "days" : "day"}.
          </b>
        </div>
      );
    } else if (status === "canceled" && subscription?.endedAt) {
      return (
        <div className="mt-8">
          The plan ended on <b>{formatDateUS(subscription.endedAt)}.</b>
        </div>
      );
    }

    return null;
  };

  return (
    <SpringContainer>
      <PageHeading heading="Billing" />

      <div className="border rounded-lg p-4 mt-4 max-w-sm">
        <div className="mb-1 text-sm">Current Plan</div>
        <div className="text-xl font-semibold flex items-center">
          {subscription?.planName}
          {subscription?.status && (
            <span className="ml-1 capitalize bg-green-200/50 p-0.5 px-2 rounded-sm text-xs text-green-900">
              {subscription?.status}
            </span>
          )}
        </div>
        {subscription?.status !== "canceled" && (
          <div className="text-gray-500 text-sm">
            {formatDateUS(startDate.toString(), false)} -{" "}
            {formatDateUS(endDate.toString())}
          </div>
        )}
        {renderSubscriptionMessage()}
      </div>

      {subscription?.status === "canceled" ? (
        <Button
          variant="primary"
          onClick={() => navigate(ROUTE_RENEW_PLANS)}
          loading={loading}
          className="mt-6"
        >
          Renew Plan
        </Button>
      ) : (
        <Button
          variant="primary"
          onClick={() => manageBilling()}
          loading={loading}
          className="mt-6 min-w-36"
        >
          Manage Billing{" "}
          <ArrowTopRightOnSquareIcon className="size-4 inline-flex" />
        </Button>
      )}
      <Button
        variant="primary"
        onClick={() =>
          window.open("https://www.articlex.com/pricing/", "_blank")
        }
        className="mt-6 ml-4"
      >
        Pricing <ArrowTopRightOnSquareIcon className="size-4 inline-flex" />
      </Button>
    </SpringContainer>
  );
};

export default Billing;
